'use client'

import { useEffect } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from '@/lib/query/client'

interface QueryProviderProps {
  children: React.ReactNode
}

// Refetch on browser back/forward (bfcache restore) so screens like
// notifications don't show stale cached state after navigating back.
// `refetchType: 'active'` only refetches queries currently mounted on
// screen, so it can't affect unrelated caching/mutation behavior.
function useRefetchOnBackForward() {
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        queryClient.invalidateQueries({ refetchType: 'active' })
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])
}

export function QueryProvider({ children }: QueryProviderProps) {
  useRefetchOnBackForward()

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}
